import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { useLoginMutation } from '../store/api/smartApi';
import { setCredentials } from '../store/slices/authSlice';
import { useAppDispatch } from '../store/hooks';
import packageJson from '../../package.json';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [login, { isLoading: loading }] = useLoginMutation();

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  const toggleLanguage = () => {
    changeLanguage(currentLanguage === 'tr' ? 'en' : 'tr');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    if (!email.trim()) {
      setLocalError(t('pages.login.errors.emailRequired'));
      return;
    }
    if (!password.trim()) {
      setLocalError(t('pages.login.errors.passwordRequired'));
      return;
    }

    try {
      const res = await login({ email: email.trim(), password }).unwrap();
      const token = res.accessToken ?? null;
      if (token && res.user) {
        dispatch(setCredentials({ token, user: res.user }));
        navigate('/workspace');
      } else {
        setLocalError(t('pages.login.errors.invalidResponse') || 'Invalid response from server.');
      }
    } catch (err: unknown) {
      const msg =
        (err as { data?: { detail?: string }; status?: number })?.data?.detail ||
        (err as { message?: string })?.message ||
        t('pages.login.errors.loginFailed') ||
        'Login failed.';
      setLocalError(msg);
    }
  };

  return (
    <div className="min-h-screen login-animated-bg flex items-center justify-center p-4 relative overflow-x-hidden font-sans">
      <div className="floating-orb floating-orb-1"></div>
      <div className="floating-orb floating-orb-2"></div>
      <div className="floating-orb floating-orb-3"></div>

      <div className="w-screen relative z-10 flex justify-center">
        <div className="w-full max-w-xs">
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-6 relative min-h-[420px]">
            <div className="absolute top-4 right-4">
              <button
                onClick={toggleLanguage}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
                aria-label="Toggle language"
                title={currentLanguage === 'tr' ? t('header.switchToEnglish') : t('header.switchToTurkish')}
              >
                {currentLanguage === 'tr' ? (
                  <>
                    <img src="https://flagcdn.com/w40/tr.png" alt="" className="w-5 h-[15px] object-cover rounded-sm shadow-sm" aria-hidden />
                    TR
                  </>
                ) : (
                  <>
                    <img src="https://flagcdn.com/w40/us.png" alt="" className="w-5 h-[15px] object-cover rounded-sm shadow-sm" aria-hidden />
                    EN
                  </>
                )}
              </button>
            </div>
            <div className="text-center mb-5">
              <div className="flex justify-center items-center -mb-3 -translate-x-5 mt-6">
                <img src="/darkLogo.png" alt="Logo" className="w-[90px] h-[100px] object-contain block" />
                <span className="text-3xl font-medium text-gray-500 dark:text-gray-300 -ml-3 font-maven">SmartPM</span>
              </div>
              <h1 className="text-2xl font-bold text-gray-500 dark:text-gray-300 mb-2 text-center">{t('pages.login.title')}</h1>
              <p className="text-gray-600 dark:text-gray-400 text-center text-[10px]">{t('pages.login.subtitle')}</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fa-solid fa-envelope text-gray-400 dark:text-gray-500"></i>
                </div>
                <input
                  ref={emailInputRef}
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (localError) setLocalError('');
                  }}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                  placeholder={t('pages.login.emailPlaceholder')}
                  autoComplete="email"
                />
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fa-solid fa-lock text-gray-400 dark:text-gray-500"></i>
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (localError) setLocalError('');
                  }}
                  className="w-full pl-10 pr-12 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                  placeholder={t('pages.login.masterPasswordPlaceholder')}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                  aria-label={showPassword ? t('pages.login.hidePassword') : t('pages.login.showPassword')}
                  title={showPassword ? t('pages.login.hidePassword') : t('pages.login.showPassword')}
                >
                  <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden />
                </button>
              </div>
              {localError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg flex items-center">
                  <i className="fa-solid fa-circle-exclamation mr-2"></i>
                  <span className="text-sm">{localError}</span>
                  <button type="button" onClick={() => setLocalError('')} className="ml-auto text-red-700 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300" aria-label={t('pages.login.closeError')}>
                    <i className="fa-solid fa-times"></i>
                  </button>
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    {t('pages.login.signingIn')}
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-arrow-right-to-bracket mr-2"></i>
                    {t('pages.login.signIn')}
                  </>
                )}
              </button>
            </form>
            <div className="flex justify-center mt-6 pt-4 border-t border-gray-200/50 dark:border-gray-600/50">
              <a href="https://netsense.com.tr" target="_blank" rel="noopener noreferrer" className="inline-block transition-all duration-200 hover:opacity-100 hover:scale-110 opacity-90 dark:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded" aria-label="Netsense - netsense.com.tr">
                <img src="/netsense-logo.png" alt="netsense business solutions" className="h-10 object-contain" />
              </a>
            </div>
          </div>
          <div className="text-center mt-4 text-sm text-gray-600 dark:text-gray-300">
            <p>{t('pages.login.footer', { year: new Date().getFullYear(), version: packageJson.version })}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
